import React, { useState, useEffect } from 'react';
import { useBalance } from '../context/BalanceContext';
import apiClient from '../api/apiClient';

const formatAmount = (value) => Number(value).toLocaleString('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const BalanceWidget = () => {
  const { refreshTrigger } = useBalance();
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { fetchBalances(); }, [refreshTrigger]);

  const fetchBalances = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/locations/balances');
      setBalances(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching balances');
      console.error('Error fetching balances:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-sm text-muted">Загрузка балансов...</div>;
  if (error) return <div className="text-sm text-accent">{error}</div>;

  const groupedBalances = balances
    .filter((balance) => !balance.is_hidden && Number(balance.current_balance) !== 0)
    .reduce((groups, balance) => {
      const currency = balance.currency || 'RUB';
      if (!groups[currency]) groups[currency] = { currency, total: 0, accounts: [] };
      groups[currency].total += Number(balance.current_balance);
      groups[currency].accounts.push(balance);
      return groups;
    }, {});
  const currencyGroups = Object.values(groupedBalances).sort((a, b) => a.currency.localeCompare(b.currency));

  return (
    <div className="space-y-6">
      <p className="text-xs uppercase tracking-[0.3em] text-muted">Баланс</p>

      {currencyGroups.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {currencyGroups.map((group) => {
            const isRub = group.currency === 'RUB';
            const accent = isRub ? 'bg-blue-400' : 'bg-accent';
            return (
              <div key={group.currency} className="relative overflow-hidden rounded-2xl border border-line bg-surface px-4 py-4">
                <span className={`absolute inset-y-4 left-0 w-1 rounded-r ${accent}`} />
                <p className={`ml-1 text-xs font-semibold uppercase tracking-[0.24em] ${isRub ? 'text-blue-400' : 'text-accent'}`}>{group.currency}</p>
                <p className="ml-1 mt-2 text-2xl font-semibold text-ink tabular-nums">{formatAmount(group.total)}</p>
                <p className="ml-1 mt-1 text-xs text-muted">{group.accounts.length} {group.accounts.length === 1 ? 'счёт' : 'счета'}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between text-xs uppercase tracking-[0.24em] text-muted">
        <span>Место</span><span>Баланс</span>
      </div>

      <div className="space-y-4">
        {currencyGroups.map((group) => {
          const isRub = group.currency === 'RUB';
          return (
            <div key={group.currency} className="overflow-hidden rounded-2xl border border-line bg-surface">
              <div className={`flex items-center justify-between border-b border-line px-4 py-3 ${isRub ? 'bg-blue-500/10' : 'bg-accent/10'}`}>
                <span className={`text-xs font-semibold uppercase tracking-[0.24em] ${isRub ? 'text-blue-400' : 'text-accent'}`}>{group.currency}</span>
                <span className="text-xs text-muted">{group.accounts.length} {group.accounts.length === 1 ? 'счёт' : 'счета'} · {formatAmount(group.total)}</span>
              </div>
              {group.accounts.map((balance) => {
                const change = Number(balance.current_balance) - Number(balance.initial_balance);
                return (
                  <div key={balance.id} className="flex items-center justify-between border-b border-line px-4 py-3 last:border-b-0">
                    <p className="text-base font-semibold text-ink">{balance.name}</p>
                    <div className="text-right">
                      <p className="text-base font-semibold text-ink tabular-nums">{formatAmount(balance.current_balance)}</p>
                      <p className={`text-xs tabular-nums ${change >= 0 ? 'text-accent' : 'text-muted'}`}>{change >= 0 ? '+' : ''}{formatAmount(change)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {currencyGroups.length === 0 && <div className="text-sm text-muted">Нет счетов с ненулевым балансом</div>}
    </div>
  );
};

export default BalanceWidget;
